import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import {
  EMT_PAGE_NAMES,
  LAST_PATH,
  OPEN_GRAPH_FACEBOOK_ADMIN,
  PAGE_TYPE,
  SD_OG_IMAGE
} from '../../constants';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';
import {
  generateStorePageCanonical,
  generateStorePageTitle,
  getGraphQlReviewsOptions
} from '../../helpers/pps-helpers';
import { emtContentModel, storeReviewsModel, storeSearchModel } from '../../models';
import { generateTags } from '../../utils/generate-tags';
import {
  generateStoreDetailDesc,
  getStoreDetailPreloadImages,
  getStoreDetailSchemas
} from './store-detail-helpers';

// --- Tag Handler ---

const storeDetailsHandler = ({
  data: { emtData, reviewData, storeData },
  props: { storeId }
}) => {
  // Extract store details data once
  const storeInfo = {
    ...storeData?.storeSearch?.stores?.[0],
    storeId
  };

  const {
    name: storeName,
    address: { city, state, postalCode } = {},
    storeDetailsPageLink
  } = storeInfo;

  const canonicalURL = generateStorePageCanonical({
    storeDetailsPageLink, urlSuffix: LAST_PATH.DETAILS
  });

  // Extract store reviews data once
  const { Includes = {}, Results = [] } = reviewData?.storeReviews || {};
  const { AverageOverallRating = 0, TotalReviewCount } = Includes?.Products?.store?.FilteredReviewStatistics || {};
  const reviewInfo = {
    AverageOverallRating,
    TotalReviewCount,
    Results
  };

  // Extract emtContent data once
  const emtContent = emtData?.emtContent?.content || {};
  const emtContentMetadata = emtContent?.metadata || {};
  const storeFinderPromotions = emtContent?.storeFinderPromotions || {};
  const topSellerMedia = emtContent?.topSeller?.products || [];
  const emtContentFileData = emtContent?.[EMT_PAGE_NAMES.DETAILS] || {};

  // Generate fallback metadata
  const fallbackTitle = generateStorePageTitle({ storeName, city, state, postalCode });
  const fallbackDescription = generateStoreDetailDesc(storeInfo);
  const {
    metaDescription = fallbackDescription,
    ogDescription = fallbackDescription,
    ogImage = SD_OG_IMAGE,
    ogTitle = fallbackTitle,
    ogType = 'PPS Main Store',
    schema: emtSchema,
    title = fallbackTitle
  } = filterEmtMetadataOverrides(emtContentMetadata);

  const preloadImages = getStoreDetailPreloadImages(topSellerMedia, storeFinderPromotions);

  const schemas = getStoreDetailSchemas({ emtSchema, storeInfo, reviewInfo, emtContentFileData });

  const tags = generateTags({
    alternate: `android-app://com.thehomedepot/homedepot/storedetails/${storeId}`,
    canonical: canonicalURL,
    description: metaDescription,
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    ogDescription,
    ogImage,
    ogSiteName: 'The Home Depot',
    ogTitle,
    ogType,
    ogUrl: canonicalURL,
    preloadImages,
    schemas,
    title
  });

  return tags;
};

// --- Main Component ---

const StoreDetailMetadata = ({ storeId }) => {
  const emtContentOptions = {
    variables: { contentId: storeId, pageName: EMT_PAGE_NAMES.DETAILS }
  };
  const reviewsOption = getGraphQlReviewsOptions({
    pageType: PAGE_TYPE.DETAILS,
    storeId
  });
  const storeSearchOptions = {
    variables: { storeSearchInput: storeId, pagesize: '1' }
  };

  const { data: emtData } = useDataModel('emtContent', emtContentOptions);
  const { data: reviewData } = useDataModel('storeReviews', reviewsOption);
  const { data: storeData } = useDataModel('storeSearch', storeSearchOptions);

  const data = { emtData, reviewData, storeData };
  const props = { storeId };

  useHelmet('storeDetailMetadata', { data, props, skipCleanup: true }, storeDetailsHandler, [storeId]);

  return null;
};

// --- Component Static Properties (dataModel, propTypes, etc.) ---

StoreDetailMetadata.displayName = 'StoreDetailMetadata';

StoreDetailMetadata.dataModel = extend(
  emtContentModel,
  storeReviewsModel,
  storeSearchModel
);

StoreDetailMetadata.propTypes = {
  storeId: PropTypes.string.isRequired
};

StoreDetailMetadata.defaultProps = {};

export { StoreDetailMetadata };